/**
 * Public front-page hero (Epic 20 S20.2, Task 2).
 *
 * Vision-first hero: headline + dual CTAs on the left, Raila's Vimeo greeting on
 * the right (equal visual weight, side-by-side ≥ md). Under `prefers-reduced-motion`
 * the video is rendered WITHOUT autoplay — a static Vimeo poster frame, no looping
 * motion (AC9). Hardcoded Finnish (Epic 20 i18n exception); hero eyebrow/subtitle
 * are prototype-verbatim per owner.
 *
 * S24.5: the JSX is written in MOBILE order (headline → video → CTAs → subtitle) so
 * mobile DOM order equals visual order; `md:` grid placement restores the desktop
 * layout. The wrapper uses `gap-x-10` (not `gap-10`) because the text column is now
 * three grid items — a row-gap would stack on top of their `mt-*` and regress the
 * desktop rhythm.
 *
 * @module components/originals/home/Hero
 */

import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../../ui/Button';
import { VimeoEmbed } from '../../video/VimeoEmbed';
import { usePrefersReducedMotion } from '../../../hooks/useMediaQuery';

// Raila — Sagamoon greeting (owner-supplied 2026-05-31, replaces the prototype id).
const HERO_VIMEO_ID = '1192370538';

export function Hero(): JSX.Element {
  const prefersReducedMotion = usePrefersReducedMotion();
  const navigate = useNavigate();

  return (
    <section className="px-4 py-12 sm:px-6 lg:px-8">
      <div className="mx-auto grid max-w-6xl items-center gap-x-10 md:grid-cols-2">
        {/* Headline. Desktop: top of the text column. */}
        <div className="md:col-start-1 md:row-start-1">
          <p className="text-sm font-semibold uppercase tracking-wide text-accent-gold">
            Suomalainen satupalvelu perheille
          </p>
          <h1 className="mt-3 font-whimsy text-4xl leading-tight text-text-twilight-cream sm:text-5xl">
            Parhaat sadut <span className="text-accent-gold">syntyvät yhdessä.</span>
          </h1>
        </div>

        {/* Raila greeting video (static poster under reduced motion). Mobile: directly
            under the h1, above the CTA. Desktop: right column, spanning the text rows. */}
        <div className="mt-8 overflow-hidden rounded-2xl shadow-lg md:col-start-2 md:row-start-1 md:mt-0 md:row-span-3">
          <VimeoEmbed
            vimeoId={HERO_VIMEO_ID}
            title="Raila — Sagamoonin tervehdys"
            autoplay={!prefersReducedMotion}
            muted
          />
        </div>

        {/* CTAs. Mobile: immediately below the video (S24.5). */}
        <div className="mt-8 flex flex-wrap items-center gap-4 md:col-start-1 md:row-start-3">
          <Button variant="golden" size="lg" type="button" onClick={() => navigate('/auth/signup')}>
            Kokeile maksutta
            <span aria-hidden="true" className="ml-1">
              →
            </span>
          </Button>
          <Link
            to="/originals"
            className="inline-flex min-h-11 items-center rounded-full border-2 border-white/20 px-5 text-sm font-medium text-text-twilight-cream/80 transition-dreamy hover:border-white/30 hover:text-text-twilight-cream focus:outline-none focus:ring-2 focus:ring-accent-gold/40"
          >
            Lue esimerkkisatuja
          </Link>
        </div>

        {/* Subtitle. Mobile: last, below both CTAs. Desktop: between h1 and CTAs. */}
        <p className="mt-5 max-w-xl text-lg text-text-twilight-cream/80 md:col-start-1 md:row-start-2">
          Sagamoonilla teette turvallisia, ikätasoon sopivia ja mielikuvituksellisia satuja
          3–10-vuotiaille lapsille. Olemme pieni suomalainen tiimi, joka haluaa elvyttää satujen
          lukuperinnettä uudella tavalla. Etsimme nyt perheitä mukaan maksuttomaan testiin.
        </p>
      </div>
    </section>
  );
}
